/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, FileText, Loader2, Pencil, Plus, Sparkles, Trash2, Upload, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { WORKBENCH_KINDS, labelForKind } from "../../lib/workbenchKinds";
import DictTextarea from "../DictTextarea";

const chip = (on) => `text-[10px] px-2 py-0.5 rounded border transition-colors ${
  on ? "border-cyan-400 bg-cyan-500/10 text-cyan-600 dark:text-cyan-200" : "border-slate-400/40 text-slate-500 hover:border-cyan-400/60"}`;

export function usePresets() {
  const [state, setState] = useState({ items: [], canEdit: false, loading: true });
  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/workbench/presets");
      setState({ items: data.items || [], canEdit: !!data.can_edit, loading: false });
    } catch { setState((s) => ({ ...s, loading: false })); }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}

const suits = (p, kind) => !p.kinds?.length || p.kinds.includes(kind);

/** Presets that suit `kind` first; `all` shows every preset so nothing is hidden. */
export function orderedPresets(items, kind, all) {
  const fit = items.filter((p) => suits(p, kind));
  return all ? [...fit, ...items.filter((p) => !suits(p, kind))] : fit;
}

const KindTags = ({ p }) => (
  <span className="text-[9px] text-[#647087]">{p.kinds?.length ? p.kinds.map(labelForKind).join(" · ") : "Any type"}</span>
);

export function PresetPicker({ items, kind, value, onChange, onManage }) {
  const [all, setAll] = useState(false);
  const list = orderedPresets(items, kind, all);
  const sel = items.find((p) => p.id === value);
  return (
    <div data-testid="workbench-preset-picker">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#a0aabf] mb-1">
        Start from a preset (optional)
        <button type="button" className="normal-case tracking-normal text-cyan-300 hover:underline" onClick={() => setAll((x) => !x)} data-testid="workbench-preset-show-all">
          {all ? "Only this type" : `Show all (${items.length})`}
        </button>
        {onManage && <button type="button" className="normal-case tracking-normal text-cyan-300 hover:underline ml-auto" onClick={onManage} data-testid="workbench-preset-manage">Manage presets</button>}
      </div>
      <div className="flex gap-1 flex-wrap">
        <button type="button" className={chip(!value)} onClick={() => onChange(null)} data-testid="workbench-preset-none">No preset</button>
        {list.map((p) => (
          <button key={p.id} type="button" className={chip(value === p.id)} onClick={() => onChange(p.id)} data-testid={`workbench-preset-${p.id}`}>
            {p.title}{p.example && <FileText className="inline w-3 h-3 ml-1 -mt-0.5" />}
          </button>
        ))}
        {list.length === 0 && <span className="text-[11px] text-[#647087]">No presets for this type yet.</span>}
      </div>
      {sel && (
        <p className="mt-2 text-[11px] text-[#a0aabf] whitespace-pre-wrap line-clamp-4 border-l-2 border-cyan-400/40 pl-2" data-testid="workbench-preset-preview">
          {sel.prompt}{sel.example && <span className="block text-cyan-300/80 mt-1">Follows example: {sel.example.filename}</span>}
        </p>
      )}
    </div>
  );
}

/** Brainstorm "Presets" button — drops a preset prompt into the message box. */
export function PresetInsertButton({ draftId, kind, onInsert }) {
  const { items, reload } = usePresets();
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const pick = async (p) => {
    setOpen(false);
    try { await api.post(`/workbench/presets/${p.id}/use`, { draft_id: draftId }); } catch { /* insert anyway */ }
    onInsert(p.prompt);
    toast.success(p.example ? `"${p.title}" added — Hidro will follow ${p.example.filename}` : `"${p.title}" added — edit freely before sending`);
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) reload(); }}>
      <PopoverTrigger asChild>
        <button type="button" className="text-[11px] inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200" data-testid="workbench-preset-insert-btn">
          <BookOpen className="w-3.5 h-3.5" /> Presets
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2 bg-[#0f1520] border-white/10" data-testid="workbench-preset-insert-menu">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-[#a0aabf] mb-1 px-1">
          {labelForKind(kind)} presets
          <button className="normal-case tracking-normal text-cyan-300 hover:underline" onClick={() => setAll((x) => !x)} data-testid="workbench-preset-insert-all">{all ? "This type" : "Show all"}</button>
        </div>
        <div className="max-h-72 overflow-auto space-y-0.5">
          {orderedPresets(items, kind, all).map((p) => (
            <button key={p.id} onClick={() => pick(p)} className="w-full text-left rounded px-2 py-1.5 hover:bg-white/5" data-testid={`workbench-preset-insert-${p.id}`}>
              <div className="text-xs text-[#e5e7eb] flex items-center gap-1">{p.title}{p.example && <FileText className="w-3 h-3 text-cyan-300" />}</div>
              <KindTags p={p} />
            </button>
          ))}
          {!orderedPresets(items, kind, all).length && <div className="text-[11px] text-[#647087] px-2 py-2">None yet — add them from the Workbench page → Prompt library.</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PresetEditor({ preset, onDone, onCancel }) {
  const [title, setTitle] = useState(preset?.title || "");
  const [prompt, setPrompt] = useState(preset?.prompt || "");
  const [kinds, setKinds] = useState(preset?.kinds || []);
  const [example, setExample] = useState(preset?.example || null);
  const [exampleIn, setExampleIn] = useState(preset?.seedExample || null);
  const [pendingFile, setPendingFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [writing, setWriting] = useState(false);
  const fileRef = useRef(null);
  const toggleKind = (k) => setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));
  const uploadPending = async () => {
    if (!pendingFile) return exampleIn;
    const fd = new FormData();
    fd.append("file", pendingFile);
    const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 0 });
    const ex = { upload_id: up.id, filename: pendingFile.name, content_type: pendingFile.type || up.content_type };
    setExampleIn(ex);
    setPendingFile(null);
    return ex;
  };
  const exampleRef = (ex) => ex || (example && { upload_id: example.upload_id, report_id: example.report_id, filename: example.filename, content_type: example.content_type });
  const writeFromExample = async () => {
    setWriting(true);
    try {
      const ex = exampleRef(await uploadPending());
      const { data } = await api.post("/workbench/presets/draft-prompt", { example: ex, instructions: prompt }, { timeout: 0 });
      setPrompt(data.prompt);
      toast.success(prompt.trim() ? "Prompt rewritten from your instructions + the example" : "Prompt written from the example — edit freely");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not write the prompt."); } finally { setWriting(false); }
  };
  useEffect(() => { if (preset?.autoWrite) writeFromExample(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const hasExample = !!(pendingFile || exampleIn || example);
  const save = async () => {
    if (!title.trim()) return toast.error("Title is required.");
    if (!prompt.trim() && !hasExample) return toast.error("Add instructions or an example document.");
    setBusy(true);
    try {
      const ex = await uploadPending();
      const body = { title, prompt, kinds, ...(ex ? { example: ex } : {}) };
      if (preset?.id) await api.patch(`/workbench/presets/${preset.id}`, { ...body, remove_example: !example && !ex }, { timeout: 0 });
      else await api.post("/workbench/presets", body, { timeout: 0 });
      toast.success("Preset saved");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save preset."); } finally { setBusy(false); }
  };
  const exName = pendingFile?.name || exampleIn?.filename || example?.filename;
  return (
    <div className="space-y-3" data-testid="workbench-preset-editor">
      <input className="hv-input w-full text-sm" placeholder="Title, e.g. Water analysis report" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="workbench-preset-title" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf] mb-1">Suggested for (none = any type)</div>
        <div className="flex gap-1 flex-wrap">
          {WORKBENCH_KINDS.map((k) => (
            <button key={k.key} type="button" className={chip(kinds.includes(k.key))} onClick={() => toggleKind(k.key)} data-testid={`workbench-preset-kind-${k.key}`}>{k.label}</button>
          ))}
        </div>
      </div>
      <DictTextarea className="hv-input w-full text-sm min-h-[160px]" placeholder="Type or dictate instructions — or leave empty and let Hidro write them from the example…" value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="workbench-preset-prompt" />
      <div className="rounded border border-white/10 p-2 text-xs space-y-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf]">Example document (optional)</div>
        <p className="text-[11px] text-[#647087]">Hidro copies its structure, tone and length — never its facts.</p>
        {exName ? (
          <div className="flex items-center gap-2" data-testid="workbench-preset-example">
            <FileText className="w-3.5 h-3.5 text-cyan-300" />
            <span className="flex-1 truncate">{exName}</span>
            <button type="button" onClick={() => { setPendingFile(null); setExample(null); setExampleIn(null); }} className="text-rose-300" data-testid="workbench-preset-example-remove"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <button type="button" className="inline-flex items-center gap-1 text-cyan-300 hover:underline" onClick={() => fileRef.current?.click()} data-testid="workbench-preset-example-upload">
            <Upload className="w-3.5 h-3.5" /> Upload example
          </button>
        )}
        <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.md,image/*" onChange={(e) => setPendingFile(e.target.files?.[0] || null)} data-testid="workbench-preset-example-input" />
        {hasExample && (
          <button type="button" disabled={writing} onClick={writeFromExample} className="mt-1 inline-flex items-center gap-1 text-cyan-300 hover:underline disabled:opacity-50" data-testid="workbench-preset-write-from-example">
            {writing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {writing ? "Hidro is studying the example…" : prompt.trim() ? "Improve prompt using the example" : "Write prompt from example"}
          </button>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" className="hv-btn hv-btn-ghost text-xs" onClick={onCancel} data-testid="workbench-preset-cancel">Cancel</button>
        <button type="button" className="hv-btn hv-btn-primary text-xs disabled:opacity-50" disabled={busy} onClick={save} data-testid="workbench-preset-save">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy && pendingFile ? "Reading example…" : "Save preset"}
        </button>
      </div>
    </div>
  );
}

export function PresetLibraryDialog({ open, onOpenChange, onChanged, seed }) {
  const { items, canEdit, reload } = usePresets();
  const [editing, setEditing] = useState(null);
  useEffect(() => { if (open && seed) setEditing(seed); }, [open, seed]);
  const [q, setQ] = useState("");
  const shown = useMemo(() => items.filter((p) => `${p.title} ${p.prompt}`.toLowerCase().includes(q.toLowerCase())), [items, q]);
  const done = () => { setEditing(null); reload(); onChanged?.(); if (seed) onOpenChange(false); };
  const remove = async (p) => {
    if (!window.confirm(`Delete preset "${p.title}"?`)) return;
    try { await api.delete(`/workbench/presets/${p.id}`); toast.success("Deleted"); done(); } catch { toast.error("Delete failed."); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setEditing(null); }}>
      <DialogContent className="max-w-2xl bg-[#0f1520] border-white/10 text-[#e5e7eb]" data-testid="workbench-preset-library">
        <DialogHeader><DialogTitle>Prompt library</DialogTitle></DialogHeader>
        {editing ? (
          <PresetEditor preset={editing === "new" ? null : editing} onDone={done} onCancel={() => { setEditing(null); if (seed) onOpenChange(false); }} />
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input className="hv-input flex-1 text-sm" placeholder="Search presets" value={q} onChange={(e) => setQ(e.target.value)} data-testid="workbench-preset-search" />
              {canEdit && <button className="hv-btn hv-btn-primary text-xs" onClick={() => setEditing("new")} data-testid="workbench-preset-new"><Plus className="w-3.5 h-3.5" /> New preset</button>}
            </div>
            <div className="max-h-[55vh] overflow-auto divide-y divide-white/5">
              {shown.map((p) => (
                <div key={p.id} className="py-2 flex gap-2 items-start" data-testid={`workbench-preset-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm flex items-center gap-1">{p.title}{p.example && <span className="text-[10px] text-cyan-300 inline-flex items-center gap-0.5"><FileText className="w-3 h-3" />{p.example.filename}</span>}</div>
                    <KindTags p={p} />
                    <p className="text-[11px] text-[#a0aabf] line-clamp-2 whitespace-pre-wrap">{p.prompt}</p>
                  </div>
                  <span className="text-[10px] text-[#647087] shrink-0">used {p.use_count || 0}×</span>
                  {canEdit && <>
                    <button onClick={() => setEditing(p)} className="text-[#a0aabf] hover:text-white" data-testid={`workbench-preset-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(p)} className="text-rose-300/80 hover:text-rose-200" data-testid={`workbench-preset-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>}
                </div>
              ))}
              {!shown.length && <div className="text-xs text-[#647087] py-6 text-center">No presets yet.{canEdit ? " Click New preset to add your first." : ""}</div>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
